import type { PDFFont, PDFPage } from 'pdf-lib';

// pdf-lib's StandardFonts (Helvetica/Courier) encode as WinAnsi, which is CP1252 rather than
// plain Latin-1: on top of the Latin-1 range it carries a block of typographic characters that
// our copy genuinely uses (em dash in ancillary labels, bullets in card masks, curly quotes and
// ellipses out of supplier data). Listing them explicitly is what keeps the itinerary PDF from
// printing "?" over legitimate text.
const WIN_ANSI_EXTRAS = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';

// CP1252 leaves 0x7F-0x9F unassigned, so those code points throw at draw time even though they
// sit inside Latin-1. Excluding them matters for mojibake in supplier feeds, which is where a
// stray C1 control byte would otherwise reach pdf-lib and fail the whole download.
const UNSUPPORTED_PATTERN = new RegExp(`[^\\u0020-\\u007E\\u00A0-\\u00FF${WIN_ANSI_EXTRAS}]`, 'g');

/**
 * Make a string safe to draw with a pdf-lib StandardFont. Anything WinAnsi cannot encode throws at
 * draw time, so arrows get an ASCII equivalent and everything else still out of range degrades to
 * "?" rather than failing the download. Embedding a Unicode font (Noto Sans) is the real fix if we
 * ever need to print CJK place names.
 */
export function safeText(text: unknown): string {
  return String(text ?? '')
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(UNSUPPORTED_PATTERN, '?');
}

// Funnel every drawText call through safeText so the renderer can't crash on
// stray Unicode in user data, no matter which code path drew it.
export function patchPageDrawText(page: PDFPage) {
  const original = page.drawText.bind(page);
  page.drawText = (text: string | number | undefined, options?: Parameters<PDFPage['drawText']>[1]) => {
    return original(safeText(text), options);
  };
}

// Same idea for measurement — wrap each font's width function once at the
// top of the render so layout uses the same character set that's drawn.
export function patchFontWidth(font: PDFFont) {
  const original = font.widthOfTextAtSize.bind(font);
  font.widthOfTextAtSize = (text: string, size: number) => original(safeText(text), size);
}
